import { shellText } from '@vnetwork/datacentral-shell/i18n'
import { injectTourStyles } from '@vnetwork/datacentral-shell/tour-styles'
import { useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { driver } from 'driver.js'
import { FeatureTour } from '@/types/tour'
import { getTourById, getToursByVersion } from '@/services/tourService'

interface UseTourLauncherOptions {
    /** Callback when tour completes */
    onComplete?: (tourId: string) => void
    /** Callback when tour exits early */
    onExit?: (tourId: string) => void
    /** Delay before starting tour (ms) */
    startDelay?: number
}

/**
 * Hook for launching guided tours using driver.js
 * Provides methods to start tours by version/ID and handles navigation
 */
export const useTourLauncher = (options: UseTourLauncherOptions = {}) => {
    const { onComplete, onExit, startDelay = 300 } = options
    const navigate = useNavigate()
    const location = useLocation()

    /**
     * Start a tour with given configuration
     */
    const startTourWithConfig = useCallback(
        (tour: FeatureTour) => {
            // Check if we need to navigate first
            const needsNavigation = !location.pathname.startsWith(tour.route)

            const initTour = () => {
                // Wait for DOM to be ready
                setTimeout(() => {
                    const driverSteps = tour.steps.map((step) => ({
                        element: step.selector,
                        popover: {
                            title: step.title,
                            description: step.content,
                            side: (step.position || 'bottom') as
                                | 'top'
                                | 'bottom'
                                | 'left'
                                | 'right',
                            align: 'center' as const,
                        },
                    }))

                    injectTourStyles()
                    const driverInstance = driver({
                        showProgress: true,
                        showButtons: ['next', 'previous', 'close'],
                        steps: driverSteps,
                        nextBtnText: shellText('tour_popover.next'),
                        prevBtnText: shellText('tour_popover.previous'),
                        doneBtnText: shellText('tour_popover.finish'),
                        progressText: '{{current}} / {{total}}',
                        onDestroyStarted: () => {
                            driverInstance.destroy()
                        },
                        onDestroyed: () => {
                            onComplete?.(tour.id)
                        },
                    })

                    driverInstance.drive()
                }, startDelay)
            }

            if (needsNavigation) {
                navigate(tour.route)
                // Wait for navigation and render
                setTimeout(initTour, 500)
            } else {
                initTour()
            }
        },
        [location.pathname, navigate, onComplete, onExit, startDelay]
    )

    /**
     * Start a tour by version and tour ID
     */
    const startTour = useCallback(
        (version: string, tourId: string) => {
            const tour = getTourById(version, tourId)
            if (tour) {
                startTourWithConfig(tour)
                return true
            }
            console.warn(`Tour not found: ${version}/${tourId}`)
            return false
        },
        [startTourWithConfig]
    )

    /**
     * Start the first tour for a version
     */
    const startFirstTourForVersion = useCallback(
        (version: string) => {
            const tours = getToursByVersion(version)
            if (tours?.length) {
                startTourWithConfig(tours[0])
                return true
            }
            console.warn(`No tours found for version: ${version}`)
            return false
        },
        [startTourWithConfig]
    )

    return {
        startTour,
        startFirstTourForVersion,
        startTourWithConfig,
    }
}
